import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Chantiers from './pages/Chantiers';
import ChantierDetail from './pages/ChantierDetail';
import NewChantier from './pages/NewChantier';
import ImportProjectWizard from './pages/ImportProjectWizard';
import ResetPassword from './pages/ResetPassword';
import SignaturePublicPage from './pages/SignaturePublicPage';
import CRCConsulterPage from './pages/CRCConsulterPage';
import VosActionsPage from './pages/VosActionsPage'; // B3 — lien « Vos actions » sans mot de passe
import Entreprises from './pages/Entreprises';
import EquipeAcces from './pages/EquipeAcces';
import Settings from './pages/Settings';
import ProfilAMO from './pages/ProfilAMO';
import EntrepriseConsultations from './pages/EntrepriseConsultations';
import MesTaches from './pages/MesTaches';
import Activation from './pages/Activation';
import MonCompte from './pages/MonCompte';
import AccesDirect from './pages/AccesDirect';
import PremierJour from './pages/PremierJour';

import './App.css';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C75B39] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6B6B6B]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route wrapper (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="w-12 h-12 border-4 border-[#C75B39] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Admin only route (plus utilisée depuis le retrait de /boamp — RP03 ; conservée
// pour les prochains écrans réservés à l'admin)
// eslint-disable-next-line no-unused-vars
function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="w-12 h-12 border-4 border-[#C75B39] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      {/* S01 / PLA-03 — inscription sur invitation uniquement */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* RP06 — invitation par lien à usage unique */}
      <Route path="/activer/:token" element={<Activation />} />
      {/* RP09 (D10 C) — lien d'accès direct des externes (sans mot de passe) */}
      <Route path="/acces/:token" element={<AccesDirect />} />
      <Route path="/signer/:token" element={<SignaturePublicPage />} />
      <Route path="/crc/consulter/:token" element={<CRCConsulterPage />} />
      <Route path="/vos-actions/:token" element={<VosActionsPage />} />

      {/* Protected Routes - Global Navigation */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/chantiers" element={<ProtectedRoute><Chantiers /></ProtectedRoute>} />
      <Route path="/chantiers/new" element={<ProtectedRoute><NewChantier /></ProtectedRoute>} />
      <Route path="/chantiers/import" element={<ProtectedRoute><ImportProjectWizard /></ProtectedRoute>} />
      <Route path="/chantiers/:id" element={<ProtectedRoute><ChantierDetail /></ProtectedRoute>} />
      <Route path="/entreprises" element={<ProtectedRoute><Entreprises /></ProtectedRoute>} />
      {/* RP84-85 — écran unique « Équipe & accès » ; anciennes pages Équipe MOE et Clients redirigées */}
      <Route path="/equipe-acces" element={<ProtectedRoute><EquipeAcces /></ProtectedRoute>} />
      <Route path="/equipe" element={<Navigate to="/equipe-acces?onglet=cabinet" replace />} />
      <Route path="/clients" element={<Navigate to="/equipe-acces?onglet=clients" replace />} />

      {/* RP03 / D37 — Page BOAMP retirée : les anciens favoris sont renvoyés à l'accueil */}
      <Route path="/boamp" element={<Navigate to="/" replace />} />
      
      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/profil" element={<ProtectedRoute><ProfilAMO /></ProtectedRoute>} />
      <Route path="/consultations" element={<ProtectedRoute><EntrepriseConsultations /></ProtectedRoute>} />
      {/* Fluidité B2 — une seule « Ma semaine » (ex-« Mes actions ») ; l'ancienne adresse redirige */}
      <Route path="/ma-semaine" element={<ProtectedRoute><MesTaches /></ProtectedRoute>} />
      <Route path="/mes-taches" element={<Navigate to="/ma-semaine" replace />} />
      {/* RP07 — Mon compte (tous les rôles) */}
      <Route path="/mon-compte" element={<ProtectedRoute><MonCompte /></ProtectedRoute>} />
      {/* RP89-92 (D11) — assistant « Premier jour » (administrateur) */}
      <Route path="/premier-jour" element={<AdminRoute><PremierJour /></AdminRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
